import React, { useEffect, useRef, useState } from 'react';
import { Button } from 'primereact/button';
import { toast } from 'react-toastify';
import Grid from '../../../components/components/Grid/Grid';
import { Spinner } from '../../../components';
import { useConfirmationModalContext } from '../../../shared/components/ModalConfirmationContext';
import { SelectOption } from '../../../shared/components/input';
import { EMPTY_GUID } from '../Model/CompanyCommissionRate';
import { CompanyRetentionRateDto, CreateCompanyRetentionRateDto, UpdateCompanyRetentionRateDto } from '../Model/CompanyRetentionRate';
import { LobPolicyTypeMapping } from '../Model/LobPolicyTypeMapping';
import CarrierManagementAPI from '../Services/CarrierManagementAPI';
import RetentionArrayService, { toIdList } from './RetentionArrayService';

export interface RetentionArraySectionConfig {
    typeId: string;
    title: string;
    messageLabel: string;
    includeRateFields: boolean;
    includeCancellationReason?: boolean;
}

interface Props {
    companyId?: string;
    parentAgency?: string;
    isActive: boolean;
    canCreate: boolean;
    canUpdate: boolean;
    canDelete: boolean;
    config: RetentionArraySectionConfig;
    stateOptions: SelectOption[];
    lineOfBusinessOptions: SelectOption[];
    policyTypeOptions: SelectOption[];
    lobPolicyTypeMappings: LobPolicyTypeMapping[];
    carrierOptions: SelectOption[];
    cancellationReasonOptions: SelectOption[];
    onDirtyChange: (typeId: string, isDirty: boolean) => void;
}

const toNullableNumber = (value: any): number | null => value === '' || value === null || value === undefined ? null : Number(value);

const normalizeLookupToken = (value: any): string => String(value || '').trim().replace(/^\{(.+)\}$/, '$1').toLowerCase();

const resolveLookupValue = (options: SelectOption[], value: any): any => {
    if (value === null || value === undefined || value === '') return '';
    const normalizedValue = normalizeLookupToken(value);
    const option = options.find(item =>
        normalizeLookupToken(item.value) === normalizedValue ||
        normalizeLookupToken(item.label) === normalizedValue ||
        normalizeLookupToken(item.originalData?.lookupCode) === normalizedValue
    );
    return option?.value || value;
};

const RetentionArraySection: React.FC<Props> = ({
    companyId, parentAgency, isActive, canCreate, canUpdate, canDelete, config,
    stateOptions, lineOfBusinessOptions, policyTypeOptions, lobPolicyTypeMappings, carrierOptions, cancellationReasonOptions, onDirtyChange
}) => {
    const modalContext = useConfirmationModalContext();
    const apiRef = useRef(new CarrierManagementAPI());
    const serviceRef = useRef(new RetentionArrayService());
    const [rows, setRows] = useState<any[]>([]);
    const [columns, setColumns] = useState<any[]>([]);
    const [isDirty, setIsDirty] = useState(false);
    const [hasFetched, setHasFetched] = useState(false);
    const [loading, setLoading] = useState(false);
    const [navigateToFirstPage, setNavigateToFirstPage] = useState(false);

    const setSectionDirty = (dirty: boolean) => {
        setIsDirty(dirty);
        onDirtyChange(config.typeId, dirty);
    };

    useEffect(() => {
        setColumns(serviceRef.current.getGridColumns(
            onEditingCellDataChange, gridCellClickHandler, stateOptions, lineOfBusinessOptions,
            policyTypeOptions, carrierOptions, !canUpdate, !canDelete, config.includeRateFields,
            lobPolicyTypeMappings,
            config.includeCancellationReason ? cancellationReasonOptions : undefined
        ));
    }, [companyId, parentAgency, stateOptions, lineOfBusinessOptions, policyTypeOptions, lobPolicyTypeMappings, carrierOptions,
        cancellationReasonOptions, canUpdate, canDelete, config.typeId, config.includeRateFields, config.includeCancellationReason]);

    useEffect(() => { setHasFetched(false); }, [companyId, parentAgency, config.typeId]);

    useEffect(() => {
        if (companyId && parentAgency && isActive && !hasFetched) fetchRows();
    }, [companyId, parentAgency, isActive, hasFetched]);

    useEffect(() => {
        if (!isActive && isDirty) {
            setRows(current => current.filter(row => row.id !== EMPTY_GUID)
                .map(row => ({ ...row, enableGridRowEditing: false, isGridInEditMode: false, editingRow: undefined })));
            setSectionDirty(false);
        }
    }, [isActive]);

    const fetchRows = async () => {
        if (!parentAgency) return;
        setLoading(true);
        try {
            const data = await apiRef.current.getRetentionRates(config.typeId, parentAgency);
            const enrichedRows = data.map((row: any) => {
                const lineOfBusinessValue = row.lineOfBusiness ?? row.lineOFBusiness ?? '';
                const enriched = {
                    ...row,
                    lineOfBusiness: resolveLookupValue(lineOfBusinessOptions, lineOfBusinessValue),
                    lineOfBusinessSourceDisplay: typeof lineOfBusinessValue === 'object'
                        ? lineOfBusinessValue.lookupValue || lineOfBusinessValue.LookupValue || lineOfBusinessValue.label || lineOfBusinessValue.Label || ''
                        : lineOfBusinessValue,
                    APlusCarrierToRate: toIdList(row.APlusCarrierToRate ?? row.aPlusCarrierToRate, true),
                    cancellationReason: toIdList(row.cancellationReason),
                    enableGridRowEditing: false,
                    isGridInEditMode: false,
                    editingRow: undefined
                };
                serviceRef.current.enrichDisplayFields(enriched, carrierOptions, lineOfBusinessOptions, policyTypeOptions);
                return enriched;
            });
            setRows(enrichedRows);
            setSectionDirty(false);
            setHasFetched(true);
        } catch {
            toast.error(`Error loading ${config.messageLabel}.`);
        } finally {
            setLoading(false);
        }
    };

    const onEditingCellDataChange = (editingRow: any, column: any, value: any, _additionalInfo: any) => {
        editingRow[column.fieldName] = value;
        if (column.fieldName === 'lineOfBusiness') editingRow.policyType = '';
        if (column.fieldName === 'percentage' && value !== '') editingRow.dollar = null;
        if (column.fieldName === 'dollar' && value !== '') editingRow.percentage = null;
        setRows(current => [...current]);
    };

    const gridCellClickHandler = async (row: any, _column: any, action: string) => {
        if (action === 'save_row') await saveRow(row);
        if (action === 'cancel_row_editing') await cancelRow(row);
        if (action === 'edit_row' && canUpdate) editRow(row);
        if (action === 'delete_row' && canDelete) await deleteRow(row);
    };

    const editRow = (row: any) => {
        row.editingRow = {
            ...row,
            lineOfBusiness: resolveLookupValue(lineOfBusinessOptions,
                row.lineOfBusiness || row.lineOfBusinessSourceDisplay || row.lineOfBusinessDisplay)
        };
        row.enableGridRowEditing = true;
        setRows(current => current.map(item => ({ ...item, isGridInEditMode: true })));
        setSectionDirty(true);
    };

    const saveRow = async (row: any) => {
        const errors = serviceRef.current.validateRow(row, config.includeRateFields, Boolean(config.includeCancellationReason));
        row.editingRow.errors = errors;
        if (Object.keys(errors).length) {
            toast.warn('Please fix validation errors before saving.');
            setRows(current => [...current]);
            return;
        }
        const editingRow = row.editingRow;
        const dto: CompanyRetentionRateDto = {
            id: editingRow.id || EMPTY_GUID,
            companyId: companyId || editingRow.companyId || '',
            parentAgency: parentAgency || editingRow.parentAgency || '',
            state: editingRow.state,
            lineOfBusiness: editingRow.lineOfBusiness,
            policyType: editingRow.policyType,
            percentage: config.includeRateFields ? toNullableNumber(editingRow.percentage) : null,
            dollar: config.includeRateFields ? toNullableNumber(editingRow.dollar) : null,
            APlusCarrierToRate: toIdList(editingRow.APlusCarrierToRate, true),
            cancellationReason: config.includeCancellationReason ? toIdList(editingRow.cancellationReason) : undefined
        };
        try {
            if (dto.id === EMPTY_GUID) {
                const createRequest: CreateCompanyRetentionRateDto = {
                    parentAgency: dto.parentAgency, retentionArrayType: config.typeId,
                    state: dto.state, lineOfBusiness: dto.lineOfBusiness, policyType: dto.policyType,
                    percentage: dto.percentage, dollar: dto.dollar, APlusCarrierToRate: dto.APlusCarrierToRate,
                    cancellationReason: dto.cancellationReason
                };
                await apiRef.current.createRetentionRate(createRequest);
                toast.success(`${config.messageLabel} created successfully.`);
            } else {
                const updateRequest: UpdateCompanyRetentionRateDto = {
                    id: dto.id, parentAgency: dto.parentAgency, retentionArrayType: config.typeId,
                    state: dto.state, lineOfBusiness: dto.lineOfBusiness, policyType: dto.policyType,
                    percentage: dto.percentage, dollar: dto.dollar, APlusCarrierToRate: dto.APlusCarrierToRate,
                    cancellationReason: dto.cancellationReason
                };
                await apiRef.current.updateRetentionRate(updateRequest);
                toast.success(`${config.messageLabel} updated successfully.`);
            }
            await fetchRows();
        } catch (error: any) {
            toast.error(error?.data?.message || error?.message || `Error saving ${config.messageLabel}.`);
        }
    };

    const cancelRow = async (row: any) => {
        if (!await modalContext.showConfirmation('Are you sure you want to discard changes?')) return;
        setRows(current => current
            .filter(item => item !== row || row.id !== EMPTY_GUID)
            .map(item => ({ ...item, enableGridRowEditing: false, isGridInEditMode: false, editingRow: undefined })));
        setSectionDirty(false);
    };

    const deleteRow = async (row: any) => {
        if (!await modalContext.showConfirmation(`Are you sure you want to delete this ${config.messageLabel}?`)) return;
        try {
            if (row.id !== EMPTY_GUID) await apiRef.current.deleteRetentionRate(row.id);
            toast.success(`${config.messageLabel} deleted successfully.`);
            await fetchRows();
        } catch (error: any) {
            toast.error(error?.data?.message || error?.message || `Error deleting ${config.messageLabel}.`);
        }
    };

    const addRow = () => {
        if (!companyId || !parentAgency) return;
        setRows(current => [serviceRef.current.getNewRow(companyId, parentAgency),
            ...current.map(row => ({ ...row, isGridInEditMode: true }))]);
        setSectionDirty(true);
        setNavigateToFirstPage(true);
    };

    return <section className="carrier-commission-grid-container p-3">
        <div className="d-flex justify-content-between align-items-center mb-3">
            <h5 className="fw-semibold mb-0">{config.title}</h5>
            <Button label="Add Row" icon="bi bi-plus-circle" onClick={addRow}
                disabled={isDirty || !canCreate} className="p-button-sm" />
        </div>
        {loading && <Spinner loading={true} type="SyncLoader" />}
        {!loading && columns.length > 0 && <div className="commission-grid-div">
            <Grid data={rows} showFilters={false} id={`retentionArrayGrid-${config.typeId}`} retainGridState={true}
                columns={columns} hiddenColumns={['selection']} sortDefault={[]} paginationDisabled={true}
                grdTabelClass="table-striped table-sm retention-array-table" grdTableContainerClass="commission-grid-table-container"
                zindexHeader={10000} navigateToFirstPage={navigateToFirstPage}
                onNavigateToFirstPage={() => setNavigateToFirstPage(false)} />
        </div>}
    </section>;
};

export default RetentionArraySection;